/* eslint-disable @typescript-eslint/no-unused-vars --
 * 本文件有一批早于本次改动的存量未使用符号（useHistoryDialog、EVENTS_CONFIG、get、
 * queryList、track），它们都配套着下方被整体注释掉的埋点与历史弹窗逻辑，等待迁移到
 * 公用组件库时一并处理（见文件内 TODO）。删掉它们会让那些注释失去上下文，
 * 不属于本次「清理调试日志」的范围，故整文件豁免。
 */
import { Toast } from 'patsnap-biz';
import { useI18n } from 'vue-i18n';
import type { NavMenuItem } from '../types';
import { NAV_MENU_TYPE } from '../types';
// import { removeHistory, removeAllRecords } from '../api';
import { cn, tw, en, jp } from '../locales';
import { useNavBarState } from './use-navbar-store';
import { useHistoryDialog } from './use-history-dialog';
import { EVENTS_CONFIG } from '../constants';
import type { IMenuItemConfig } from '../IpNavService';
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { type HeaderService } from '@patsnap-utils/biz';
import { filter, get, map } from 'lodash-es';

// TODO: 迁移到公用组件库时需要移除 @eureka 依赖
// import { CommonApis } from '@eureka/services/src/apis/common.api';
// import type { IControl } from '@eureka/utils/control';
// import { controlDeferred } from '@eureka/utils/control';

type HeaderServiceWithExtendAttrs = HeaderService & {
    ignoreDetailIdDetect: boolean // 忽略详情id检测, 用于某些特殊场景下, 比如monitor
}

export const useNavBar = (props: {
    navModuleType: NAV_MENU_TYPE;
    ipNavService?: {
        getMenuConfig: () => IMenuItemConfig[];
        headerService?: HeaderServiceWithExtendAttrs;
        navigateTo?: (menu: NavMenuItem) => void;
    };
    confirmDialogRef?: any;
}) => {
    const headerService = computed(() => props.ipNavService?.headerService) as any;
    const { t } = useI18n({
        messages: {
            cn, en, jp, tw,
        },
    });

    const historyPreTitleMessages = ref({
        cn: {
            HistoryType: cn.HistoryType,
        },
        en: {
            HistoryType: en.HistoryType,
        },
        jp: {
            HistoryType: jp.HistoryType,
        },
        tw: {
            HistoryType: tw.HistoryType,
        },
    });

    // TODO: 迁移到公用组件库时需要移除或改为从外部传入
    // const control = ref<IControl>();
    const control = ref<Record<string, boolean>>({});

    const route = useRoute();

    // const router = useRouter();

    const { historyList, updateHistory, queryList, activeMenuType } = useNavBarState();

    // 原始分组历史数据
    const historyMaps = computed(() => {
        return (props.ipNavService as any)?.historyMaps?.value || [];
    });

    const navHistory = computed(() => {
        return {
            /**
             * 为了兼容组件库，暂时先添加 id 属性
             */
            id: 'history',
            type: NAV_MENU_TYPE.HISTORY,
            icon: 'SolidTimeRefresh',
            title: t(`NavBar.${NAV_MENU_TYPE.HISTORY}`),
            children: historyList.value.slice(0, 10),
        };
    });

    async function confirmSaveBoardChange(data: { title?: string; content?: string } = {}) {
        const dialogRef = props.confirmDialogRef?.value;

        if (!dialogRef || typeof dialogRef.open !== 'function') {
            return Promise.resolve(true);
        }

        return dialogRef.open({
            title: data.title || t('NavBar.deleteTitle'),
            message: data.content || t('NavBar.deleteContent'),
            confirmButtonText: t('NavBar.confirm'),
            cancelButtonText: t('NavBar.cancel'),
        });
    }

    async function removeHistoryItem(historyId: string) {
        const confirm = await confirmSaveBoardChange();
        if (!confirm) return;

        // 找到对应的历史记录项
        const item = historyList.value.find(nav => nav.id === historyId);
        if (!item) return;

        try {
            // 调用 ipNavService 的删除方法
            await (props.ipNavService as any)?.removeHistory?.(item);

            // 同步更新本地状态
            updateHistory(filter(historyList.value, nav => nav.id !== historyId));

            Toast({
                message: t('NavBar.deleteSuccess'),
                type: 'success',
            });
        } catch (error) {
            console.error('[removeHistoryItem] Failed:', error);
        }
    }

    /**
     * 同一个模块内使用vue-router跳转
     * 不同模块使用service的基础navigateTo跳转
     */
    function navigateTo(menu: NavMenuItem) {
        // 拦截 SEARCH_TASKS，打开历史记录 dialog
        // if (menu.type === NAV_MENU_TYPE.SEARCH_TASKS) {
        //     const { open } = useHistoryDialog();
        //     open();
        //     return;
        // }

        // const isSamePackage = menu.module === props.navModuleType || menu.module === route.meta.navModuleType
        // if (isSamePackage && menu.router) {
        //     router.push(menu.router)
        // } else {
        // 调用service的基础navigateTo方法，支持外部覆写
        props.ipNavService?.navigateTo?.(menu);
        // }
    }

    // 埋点实现整体被注释（见下方 TODO），参数以 _ 前缀标记为有意未使用。
    function track(_type: string, _detail: string) {
        // TODO: 迁移到公用组件库时需要移除 @eureka 依赖
        // CommonApis.track({
        //     event_type: _type,
        //     event_detail_str: _detail,
        //     module_type: 'EUREKA',
        //     source_type: 'air',
        //     other_param: route.name as string,
        // })
    }

    function viewResult(menu: NavMenuItem) {
        navigateTo(menu);

        const { setActiveMenuType: setType } = useNavBarState();
        setType(menu.type, menu.id);

        // const event = get(EVENTS_CONFIG, menu.type, EVENTS_CONFIG.HOME)
        // track(event, menu.type);
    }

    function onRemoveHistory(data: NavMenuItem) {
        if (!data.id) return;
        removeHistoryItem(data.id);
    }

    async function removeAllHistory() {
        const confirm = await confirmSaveBoardChange({
            title: t('NavBar.deleteAllTitle'),
            content: t('NavBar.deleteAllContent'),
        });
        if (!confirm) return;

        try {
            // 调用 ipNavService 的删除所有方法
            await (props.ipNavService as any)?.removeAllHistory?.();

            // 同步更新本地状态
            updateHistory([]);

            Toast({
                message: t('NavBar.deleteSuccess'),
                type: 'success',
            });
        } catch (error) {
            console.error('[removeAllHistory] Failed:', error);
        }
    }

    /**
     * 判断菜单是否高亮
     * 优先级：
     * 1. 手动设置的 activeMenuType（当 controlled 为 true 时）
     * 2. 路由 meta 中的 navModuleType
     * 3. props.navModuleType
     */
    const handleMenuActive = (item: NavMenuItem, isChild: boolean = false) => {
        // 有子菜单的项不参与高亮判断
        if (item.children?.length && !isChild) {
            return false;
        }

        const { controlled } = useNavBarState();

        // 如果由外部控制（onNavigate 回调），只使用 activeMenuType
        if (controlled.value) {
            return item.type === activeMenuType.value;
        }

        // 使用手动设置的高亮类型
        if (activeMenuType.value) {
            return item.type === activeMenuType.value;
        }

        // 使用路由 meta
        if (route.meta.navModuleType) {
            return item.type === route.meta.navModuleType;
        }

        // 最后使用 props.navModuleType
        return item.type === props.navModuleType;
    };

    /**
     * 设置高亮菜单类型（对外暴露的方法）
     */
    function setActiveMenuType(type: NAV_MENU_TYPE | string | null) {
        const { setActiveMenuType: setType } = useNavBarState();
        setType(type);
    }

    /**
     * 清除高亮
     */
    function clearActiveMenuType() {
        const { clearActiveMenuType: clearType } = useNavBarState();
        clearType();
    }

    const navMenuList = computed(() => {
        const menus = props.ipNavService?.getMenuConfig() || [];
        const isLogin = headerService?.value?.userCenterService?.logged;
        const filteredMenus = menus.filter(item => {
            if (item.needLogin && !isLogin) {
                return false;
            }
            return true;
        });

        return map(filteredMenus, item => {
            return {
                ...item,
                /**
                 * 为了兼容组件库，暂时先添加 id 属性
                 */
                id: item.type,
                title: t(`NavBar.${item.type}`),
                active: handleMenuActive(item as any, false),
                onClick: viewResult,
                children: map(item.children, child => {
                    /**
                     * 子菜单使用 handleMenuActive 判断高亮
                     */
                    return {
                        ...child,
                        title: t(`NavBar.${child.type}`),
                        active: handleMenuActive(child as any, true),
                        onClick: viewResult,
                    };
                }),
            };
        });
    });

    onMounted(async() => {
        // TODO: 迁移到公用组件库时需要移除 @eureka 依赖，改为从外部传入 controlDeferred
        // control.value = await controlDeferred.promise;
        control.value = {};

        // 从 ipNavService 加载历史记录
        if (props.ipNavService) {
            await (props.ipNavService as any).queryHistoryList?.();
            // 同步 ipNavService 的历史记录到本地状态
            const serviceHistoryList = (props.ipNavService as any).historyList?.value || [];
            if (serviceHistoryList.length > 0) {
                updateHistory(serviceHistoryList);
            }
        }
    });

    return {
        historyPreTitleMessages,
        viewResult,
        removeHistoryItem,
        removeAllHistory,
        onRemoveHistory,
        navHistory,
        historyMaps,
        navMenuList,
        setActiveMenuType,
        clearActiveMenuType,
    };
};
